import { create } from 'zustand';
import type { Connection, ExecuteResponse, QueryResult } from '@/types/domain';
import { connectionsApi } from '@/api/connections';

export interface EditorTab {
  id: string;
  title: string;
  sqlText: string;
  connectionId?: string;
  dirty: boolean;
  lastResult?: QueryResult;
}

interface EditorState {
  // connections
  connections: Connection[];
  connectionsLoading: boolean;
  connectionsError?: string;
  selectedConnectionId?: string;

  // editor tabs
  tabs: EditorTab[];
  activeTabId?: string;
  isExecuting: boolean;
  lastResponse?: ExecuteResponse;

  // actions
  loadConnections: () => Promise<void>;
  selectConnection: (id: string) => void;
  newTab: (initialSql?: string) => void;
  closeTab: (id: string) => void;
  setActiveTab: (id: string) => void;
  updateTabSql: (id: string, sql: string) => void;
  setExecuting: (flag: boolean) => void;
  setLastResponse: (resp: ExecuteResponse) => void;
}

let tabSeq = 1;

export const useEditorStore = create<EditorState>((set, get) => ({
  connections: [],
  connectionsLoading: false,
  tabs: [{ id: 'tab-1', title: 'Query 1', sqlText: '', dirty: false }],
  activeTabId: 'tab-1',
  isExecuting: false,

  loadConnections: async () => {
    set({ connectionsLoading: true, connectionsError: undefined });
    try {
      const connections = await connectionsApi.list();
      set({ connections, connectionsLoading: false });
    } catch (e) {
      set({ connectionsLoading: false, connectionsError: 'Falha ao carregar conexões' });
    }
  },

  selectConnection: (id) => {
    const conn = get().connections.find(c => c.id === id);
    set({ selectedConnectionId: id });
    // Atualiza a aba ativa com a conexão selecionada
    const { tabs, activeTabId } = get();
    if (activeTabId) {
      const updatedTabs = tabs.map(t =>
        t.id === activeTabId ? { ...t, connectionId: id, title: conn ? `${conn.database}` : t.title } : t
      );
      set({ tabs: updatedTabs });
    }
  },

  newTab: (initialSql = '') =>
    set((state) => {
      tabSeq += 1;
      const id = `tab-${tabSeq}`;
      return {
        tabs: [...state.tabs, {
          id,
          title: `Query ${tabSeq}`,
          sqlText: initialSql,
          connectionId: state.selectedConnectionId,
          dirty: false,
        }],
        activeTabId: id,
      };
    }),

  closeTab: (id) =>
    set((state) => {
      const tabs = state.tabs.filter(t => t.id !== id);
      const activeTabId = state.activeTabId === id
        ? (tabs.at(-1)?.id ?? undefined)
        : state.activeTabId;
      return { tabs: tabs.length > 0 ? tabs : [{ id: 'tab-1', title: 'Query 1', sqlText: '', dirty: false }], activeTabId };
    }),

  setActiveTab: (id) => set({ activeTabId: id }),

  updateTabSql: (id, sqlText) =>
    set((state) => ({
      tabs: state.tabs.map(t => t.id === id ? { ...t, sqlText, dirty: true } : t),
    })),

  setExecuting: (isExecuting) => set({ isExecuting }),
  setLastResponse: (lastResponse) => set({ lastResponse }),
}));
